import { ApiError } from './errors';
import { TOKEN_STORAGE_KEYS, createTokenStorage } from './storage';
import type { ClientKind, StoredTokens, TokenStorage } from './storage';
import type { AuthTokens, ProblemDetails, SessionTokens } from './types';

export type QueryValue = string | number | boolean | undefined | null;
export type Query = Record<string, QueryValue>;

export interface RequestOptions {
  /** Query string values; undefined/null entries are dropped. */
  query?: Query;
  /** JSON body. Use `form` for the (rare) form-encoded endpoint. */
  body?: unknown;
  form?: Record<string, string>;
  /** Send the bearer token (default true for everything except the anonymous endpoints). */
  auth?: boolean;
  /** Do not attempt a token refresh on 401 (used by the refresh call itself). */
  skipRefresh?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export interface HttpCore {
  readonly kind: ClientKind;
  readonly baseUrl: string;
  readonly tenantSlug: string;
  request<T>(method: string, path: string, options?: RequestOptions): Promise<T>;
  /** GET a binary document (invoice PDF) with auth + refresh. */
  blob(path: string, options?: RequestOptions): Promise<Blob>;
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  del<T>(path: string, options?: RequestOptions): Promise<T>;
  /** Tokens currently in storage (null when signed out). */
  getTokens(): StoredTokens | null;
  /** Accepts a session without a refresh token: it then ends when the access token expires. */
  setTokens(tokens: SessionTokens | null): void;
  isAuthenticated(): boolean;
  /** Subscribe to sign-in / sign-out / refresh. Returns an unsubscribe function. */
  onTokensChanged(listener: (tokens: StoredTokens | null) => void): () => void;
  /**
   * Subscribe to "the demo account may not do that": a 403 whose problem code is `demo_read_only`.
   *
   * The shared demo accounts are read-only and the server is the boundary, so any write from one comes
   * back refused. That is not a failure worth an error page or a sign-out — it is the demo working as
   * intended — so it is announced here instead, once per request, and the app turns it into a toast in
   * the reader's own language. The call still rejects with the ApiError, exactly like any other 403.
   * Returns an unsubscribe function.
   */
  onDemoReadOnly(listener: (error: ApiError) => void): () => void;
}

export interface HttpCoreOptions {
  /** '' means "same origin" (dev proxy or same-host ingress). */
  baseUrl?: string;
  /** Tenant slug for the X-Tenant header. */
  tenant?: string;
  kind?: ClientKind;
  storage?: TokenStorage;
  /** Called once when a refresh definitively fails — the app should route to /login. */
  onAuthLost?: () => void;
  fetchImpl?: typeof fetch;
}

/** Path prefix of the versioned API. Joined onto `baseUrl`. */
export const API_PREFIX = '/api/v1';

export function createHttpCore(options: HttpCoreOptions = {}): HttpCore {
  const kind: ClientKind = options.kind ?? 'customer';
  const baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '');
  const tenantSlug = options.tenant ?? '';   // empty → no X-Tenant header; the server resolves by hostname (ADR-0007)
  const storage = options.storage ?? createTokenStorage(TOKEN_STORAGE_KEYS[kind]);
  const doFetch: typeof fetch = options.fetchImpl ?? ((...a) => globalThis.fetch(...a));
  const refreshPath = kind === 'admin' ? '/admin/auth/refresh' : '/auth/refresh';
  const listeners = new Set<(t: StoredTokens | null) => void>();
  const demoListeners = new Set<(e: ApiError) => void>();

  /** Single-flight refresh: concurrent 401s wait on the same promise. */
  let refreshInFlight: Promise<StoredTokens | null> | null = null;

  function emit(tokens: StoredTokens | null) {
    for (const l of listeners) l(tokens);
  }

  function setTokens(tokens: SessionTokens | null) {
    if (!tokens) {
      storage.write(null);
      emit(null);
      return;
    }
    const stored: StoredTokens = {
      ...tokens,
      expiresAt: tokens.expiresIn ? Date.now() + tokens.expiresIn * 1000 : undefined,
    };
    storage.write(stored);
    emit(stored);
  }

  /**
   * The one place a failed response becomes an ApiError — and therefore the one place that can notice a
   * demo session being refused a write. No redirect and no console noise: the listeners decide what the
   * reader is told, and the error is thrown afterwards like any other.
   */
  function toError(status: number, problem: ProblemDetails): ApiError {
    const error = ApiError.fromProblem(status, problem);
    if (error.status === 403 && error.code === 'demo_read_only') {
      for (const l of demoListeners) l(error);
    }
    return error;
  }

  function buildUrl(path: string, query?: Query): string {
    const url = `${baseUrl}${API_PREFIX}${path.startsWith('/') ? path : `/${path}`}`;
    if (!query) return url;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      qs.append(k, String(v));
    }
    const s = qs.toString();
    return s ? `${url}?${s}` : url;
  }

  async function refresh(): Promise<StoredTokens | null> {
    if (refreshInFlight) return refreshInFlight;
    const current = storage.read();
    if (!current?.refreshToken) return null;
    refreshInFlight = (async () => {
      try {
        const next = await request<AuthTokens>('POST', refreshPath, {
          body: { refreshToken: current.refreshToken },
          auth: false,
          skipRefresh: true,
        });
        setTokens(next);
        return storage.read();
      } catch {
        setTokens(null);
        options.onAuthLost?.();
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  }

  async function parseBody(res: Response): Promise<unknown> {
    if (res.status === 204 || res.status === 205) return undefined;
    const text = await res.text();
    if (!text) return undefined;
    const type = res.headers.get('content-type') ?? '';
    if (type.includes('json')) {
      try {
        return JSON.parse(text) as unknown;
      } catch {
        return text;
      }
    }
    return text;
  }

  async function send(method: string, path: string, opts: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(tenantSlug ? { 'X-Tenant': tenantSlug } : {}),
      ...opts.headers,
    };
    let body: BodyInit | undefined;
    if (opts.form) {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
      body = new URLSearchParams(opts.form).toString();
    } else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    if (opts.auth !== false) {
      const token = storage.read()?.accessToken;
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    try {
      return await doFetch(buildUrl(path, opts.query), {
        method,
        headers,
        body,
        signal: opts.signal,
        credentials: 'omit',
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw ApiError.network(e instanceof Error ? e.message : 'Network request failed');
    }
  }

  async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    let res = await send(method, path, opts);

    if (res.status === 401 && opts.auth !== false && !opts.skipRefresh && storage.read()?.refreshToken) {
      const refreshed = await refresh();
      if (refreshed) res = await send(method, path, { ...opts, skipRefresh: true });
    }

    const payload = await parseBody(res);
    if (!res.ok) {
      if (res.status === 401 && opts.auth !== false) {
        // Definitively signed out: drop the tokens so guards react immediately.
        if (storage.read()) {
          setTokens(null);
          options.onAuthLost?.();
        }
      }
      const problem: ProblemDetails =
        payload && typeof payload === 'object'
          ? (payload as ProblemDetails)
          : { title: typeof payload === 'string' && payload ? payload : res.statusText, status: res.status };
      throw toError(res.status, problem);
    }
    return payload as T;
  }

  /**
   * Fetches a binary document (an invoice PDF) with the same auth and refresh behaviour as `request`.
   * A plain <a href> cannot be used: the endpoint needs the Bearer token.
   */
  async function blob(path: string, opts: RequestOptions = {}): Promise<Blob> {
    let res = await send('GET', path, opts);
    if (res.status === 401 && opts.auth !== false && !opts.skipRefresh && storage.read()?.refreshToken) {
      const refreshed = await refresh();
      if (refreshed) res = await send('GET', path, { ...opts, skipRefresh: true });
    }
    if (!res.ok) {
      const payload = await parseBody(res);
      const problem: ProblemDetails =
        payload && typeof payload === 'object'
          ? (payload as ProblemDetails)
          : { title: res.statusText, status: res.status };
      throw toError(res.status, problem);
    }
    return res.blob();
  }

  return {
    kind,
    baseUrl,
    tenantSlug,
    request,
    blob,
    get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>('POST', path, { ...opts, body }),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>('PATCH', path, { ...opts, body }),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>('PUT', path, { ...opts, body }),
    del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
    getTokens: () => storage.read(),
    setTokens,
    isAuthenticated: () => Boolean(storage.read()?.accessToken),
    onTokensChanged(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    onDemoReadOnly(listener) {
      demoListeners.add(listener);
      return () => demoListeners.delete(listener);
    },
  };
}
